// The drafts a receipt's cards have ALREADY been shown, kept across reloads.
//
// WHY THIS EXISTS. Reopening "Initial outreach — 50 new leads" used to rebuild
// every card's email from scratch on mount, with the drafting spinner playing
// over the top of it. The builders are pure and cheap, so nothing was actually
// re-spent — but the research BEHIND those facts is not free, and a screen full
// of spinners on a reload reads exactly like money burning. This store is the
// answer to both halves: the draft is built once per lead per set of facts, and
// a reload that finds it here paints the email with no beat, because no work is
// being done.
//
// WHAT IT IS NOT. It is not a source of facts. Nothing in here is ever merged
// into a draft input, nothing here triggers enrichment or research, and a miss
// costs a rebuild from the receipt's own stub — never a fetch. The cache can be
// wiped at any moment (quota, another tab, a version bump) and the only visible
// difference is the beat playing once more.
//
// SCOPED PER ACCOUNT, same as every other browser-local overlay: a draft written
// for one signed-in user must never surface under another. scopedKey() resolves
// against whoever is signed in RIGHT NOW, so it is called per read/write and the
// in-memory mirror below is invalidated whenever it changes.
import { scopedKey } from './userScope.js'

/**
 * Bump this whenever the DRAFT BUILDERS change their output — outreachDraft.js,
 * the humanize pass, or the shape LeadCard stores. It rides in every signature
 * AND is stamped on the store, so a bump both fails every lookup and drops the
 * old entries wholesale on the next load. Without it a code change would keep
 * serving last week's wording out of localStorage forever.
 */
export const DRAFT_VERSION = 1

const STORAGE_BASE = 'revailosales.draftCache'
const storageKey = () => scopedKey(STORAGE_BASE)

// Bounds. Two of them, because either one alone leaks: 400 tiny call-point
// entries are nothing, 400 researched emails are most of a megabyte. Whichever
// binds first wins, and shedding is always oldest-used-first.
const MAX_ENTRIES = 400
const MAX_BYTES = 400_000

// Persisted shape: { v: DRAFT_VERSION, e: { [slot]: { k: leadKey, s: sig, v: value, u: use } } }
//
// `slot` is leadKey + signature, NOT leadKey alone — one lead may legitimately
// have more than one live draft. A thread can show the same company on an old
// initial-outreach receipt and on today's follow-up receipt, and those two
// cards read different facts and sign differently. One slot per lead would have
// them evicting each other on every render: a permanent miss for both, and the
// drafting beat back on every reload, which is the exact bug this file exists
// to kill. Superseded variants are not special-cased — they simply stop being
// used, and the LRU cap collects them.
//
// `u` is a logical clock, not a timestamp: LRU only needs an order, and an
// order that cannot be scrambled by a clock change or two tabs disagreeing
// about the time is strictly the safer one.
// NUL as the separator: a signature is JSON and can contain any printable
// character, so a printable separator could be forged into a slot name. Even
// then get() still compares the STORED signature before serving — the slot is
// an index, never the proof.
const SLOT_SEP = '\u0000'
const slotFor = (leadKey, sig) => `${leadKey}${SLOT_SEP}${sig}`

let mirror = null // the parsed entry map, or null when nothing is loaded
let mirrorKey = '' // the storageKey() `mirror` was loaded from
let clock = 0

/**
 * Deterministic stringify — keys sorted, undefined dropped, depth-capped.
 *
 * Signatures compare ACROSS reloads, so insertion order must not enter them:
 * the same facts arriving as a snapshot-then-stub merge in one session and a
 * stub-only merge in the next would otherwise sign differently and rebuild for
 * no reason. The depth cap is the cycle guard — a fact object should never be
 * more than a couple of levels deep, and a runaway one returns a sig the caller
 * rejects rather than blowing the render stack.
 */
function stable(value, depth = 0) {
  if (depth > 8) return null
  if (value === undefined) return 'null'
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) {
    const parts = []
    for (const item of value) {
      const part = stable(item, depth + 1)
      if (part === null) return null
      parts.push(part)
    }
    return `[${parts.join(',')}]`
  }
  const parts = []
  for (const k of Object.keys(value).sort()) {
    if (value[k] === undefined) continue
    const part = stable(value[k], depth + 1)
    if (part === null) return null
    parts.push(`${JSON.stringify(k)}:${part}`)
  }
  return `{${parts.join(',')}}`
}

/**
 * The content signature for a draft: everything the draft was built FROM, plus
 * DRAFT_VERSION. A lead whose facts changed signs differently and rebuilds; a
 * builder change signs differently for every lead at once.
 *
 * Returns '' for anything that cannot be signed honestly (a cycle, a value the
 * cap refused). get()/put() both treat '' as "do not cache" — an unsignable
 * input falls back to today's behaviour, which is simply to build it.
 */
export function signature(parts) {
  const body = stable(parts)
  return body === null ? '' : `${DRAFT_VERSION}|${body}`
}

function load() {
  let k = ''
  try {
    k = storageKey()
  } catch {
    k = ''
  }
  if (mirror && mirrorKey === k) return mirror
  mirrorKey = k
  clock = 0
  let parsed = null
  try {
    parsed = JSON.parse(localStorage.getItem(k) || 'null')
  } catch {
    // Corrupt JSON, private mode, no localStorage at all. An unreadable store
    // is an EMPTY store: the cards rebuild, exactly as they did before this
    // file existed. It is never a throw into a render.
    parsed = null
  }
  const out = {}
  const entries =
    parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.v === DRAFT_VERSION
      ? parsed.e
      : null
  if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
    for (const [slot, rec] of Object.entries(entries)) {
      // Every field is re-validated. This blob is user-writable (it is
      // localStorage), and a record that does not carry a signature cannot be
      // matched against one — so it is not a cache entry, it is noise.
      if (!slot || !rec || typeof rec !== 'object' || Array.isArray(rec)) continue
      if (typeof rec.s !== 'string' || !rec.s) continue
      if (typeof rec.k !== 'string' || !rec.k) continue
      if (rec.v === undefined || rec.v === null) continue
      const u = Number(rec.u)
      const use = Number.isFinite(u) ? u : 0
      out[slot] = { k: rec.k, s: rec.s, v: rec.v, u: use }
      if (use > clock) clock = use
    }
  }
  mirror = out
  return mirror
}

function serialize(entries) {
  try {
    return JSON.stringify({ v: DRAFT_VERSION, e: entries })
  } catch {
    return ''
  }
}

/**
 * Persist the mirror, shedding oldest-used-first until it fits BOTH bounds.
 * Whatever is shed is dropped from the mirror too, so memory and storage never
 * disagree about what is cached.
 */
function flush() {
  const store = mirror || {}
  // Oldest use first — the head of this list is what goes.
  const order = Object.entries(store).sort((a, b) => a[1].u - b[1].u)
  while (order.length > MAX_ENTRIES) {
    const [victim] = order.shift()
    delete store[victim]
  }
  let json = serialize(Object.fromEntries(order))
  while (json && json.length > MAX_BYTES && order.length > 0) {
    const [victim] = order.shift()
    delete store[victim]
    json = serialize(Object.fromEntries(order))
  }
  if (!json) json = serialize({})
  try {
    localStorage.setItem(mirrorKey, json)
  } catch {
    // Quota, or a browser that refuses to write at all. Try once with the
    // newest quarter — the entries on screen right now — and if that is still
    // refused, give the space back and carry on serving from memory for the
    // rest of this session. A cache that cannot persist is still a cache.
    const keep = order.slice(Math.max(0, order.length - Math.ceil(MAX_ENTRIES / 4)))
    for (const [victim] of order.slice(0, order.length - keep.length)) delete store[victim]
    try {
      localStorage.setItem(mirrorKey, serialize(Object.fromEntries(keep)))
    } catch {
      try {
        localStorage.removeItem(mirrorKey)
      } catch {
        /* nothing left to try — the session keeps its in-memory hits */
      }
    }
  }
}

/**
 * The cached value for this lead, but ONLY if it was built from the same
 * signature. Returns null on any miss — no entry, a stale signature, an
 * unreadable store. Never throws.
 *
 * A hit counts as a use (LRU), recorded in memory and persisted on the next
 * put(); an all-hit reload does not write to localStorage at all, which is the
 * whole point of the reload path.
 */
export function get(leadKey, sig) {
  const k = String(leadKey || '')
  if (!k || !sig) return null
  const rec = load()[slotFor(k, sig)]
  // The signature is checked AGAIN here even though it is half the slot name:
  // the slot is an index into a blob anyone can edit, and only the stored
  // signature is evidence that this value was built from these facts.
  if (!rec || rec.s !== sig || rec.k !== k) return null
  clock += 1
  rec.u = clock
  return rec.v
}

/**
 * Remember a built value under this lead + signature, and return the value the
 * caller should actually render.
 *
 * That return is the important part: what comes back is the JSON round-trip of
 * what went in — the very bytes a reload will read. Rendering the round-trip
 * instead of the original is what makes "the draft after a refresh is identical
 * to the draft before it" true by construction rather than by inspection.
 *
 * Returns null when the value cannot be cached (unserializable, or larger on
 * its own than the whole budget); the caller then renders what it built and
 * simply rebuilds next time.
 */
export function put(leadKey, sig, value) {
  const k = String(leadKey || '')
  if (!k || !sig || value === undefined || value === null) return null
  const store = load()
  let clone = null
  let json = ''
  try {
    json = JSON.stringify(value)
    clone = JSON.parse(json)
  } catch {
    return null
  }
  // One oversized draft must not be able to evict every other entry to make
  // room for itself. It is simply not cached.
  if (!json || json.length > MAX_BYTES) return null
  const slot = slotFor(k, sig)
  clock += 1
  store[slot] = { k, s: sig, v: clone, u: clock }
  flush()
  // flush() may have shed this very entry (a store already at the byte cap and
  // this the oldest use — possible only if the clock was restored oddly). Say
  // so honestly rather than reporting a hit that will not be there.
  return store[slot] ? clone : null
}

/**
 * BUILD ONCE. The whole point of this module, in one call: a hit is returned
 * with `prebuilt: true` and `build` is never invoked; a miss builds, stores,
 * and comes back with `prebuilt: false` and the stored round-trip as the value.
 *
 * `prebuilt` is what a card gates its "drafting…" beat on. It is a statement
 * about work, not about storage: false means this build really just happened,
 * true means it happened before and this is a record being reopened.
 *
 * An unusable key or signature ('' — see signature()) is not an error: build
 * runs, nothing is cached, and the caller behaves exactly as it did before the
 * cache existed.
 */
export function resolve(leadKey, sig, build) {
  if (!leadKey || !sig) return { value: build(), prebuilt: false }
  const hit = get(leadKey, sig)
  if (hit !== null) return { value: hit, prebuilt: true }
  const value = build()
  const stored = put(leadKey, sig, value)
  return { value: stored === null ? value : stored, prebuilt: false }
}

/**
 * Every lead key currently cached, for the account signed in right now —
 * deduplicated, because one lead may hold more than one variant (see the note
 * on slots above). Use stats() when you want the raw entry count.
 */
export function listKeys() {
  return [...new Set(Object.values(load()).map((rec) => rec.k))]
}

/** Drop everything for the current account. */
export function clear() {
  load()
  mirror = {}
  clock = 0
  try {
    localStorage.removeItem(mirrorKey)
  } catch {
    /* nothing persisted anyway */
  }
}

/** Entry count and serialized size — for diagnostics and the harness. */
export function stats() {
  const store = load()
  const json = serialize(store)
  return { entries: Object.keys(store).length, bytes: json.length, version: DRAFT_VERSION }
}

/**
 * Forget the in-memory mirror WITHOUT touching what is stored — the next read
 * re-parses localStorage from scratch. This is what a page reload does, and it
 * is the only way to prove the reload path really returns the stored bytes.
 */
export function __resetMemory() {
  mirror = null
  mirrorKey = ''
  clock = 0
}
